import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, useHistory, useLocation } from "react-router-dom";
import { CompatRouter } from "react-router-dom-v5-compat";
import { jsonFromDocument } from "cerulean";
import SettingsPageRouter, {
  groupPath,
  settingPath,
  useRouterPathParams,
} from "./SettingsPageRouter";

const getBaseUrl = (pageName = "institution_settings") => {
  const path = window.location.pathname;
  const i = path.indexOf(pageName);
  return i === -1 ? path : path.substring(0, i + pageName.length);
};

function SettingsPage({ settings, initialState }) {
  const defaultGroup = initialState.group || Object.keys(settings)[0];
  const defaultSubgroup = initialState.subgroup || Object.keys(settings[defaultGroup])[0];
  const attemptedValue = initialState.attempted_value;

  const [error, setError] = useState(initialState.error || null);
  const [errorWasShown, setErrorWasShown] = useState(!error);

  const { group, subgroup, settingName } = useRouterPathParams();
  const history = useHistory();

  const qp = new URLSearchParams(useLocation().search);

  useEffect(() => {
    /*
      An initialState.setting is only set when an error is returned.

      On first mount, either:
        1. If routing to "sync", navigate to the sync settings page
        2. If there was an error, navigate to the offending setting page.
        3. If the route is '/', navigate to the first group & subgroup.
        4. If the group and subgroup route is user-entered, reformat and prettify
        5. If setting name is user-entered, reformat and prettify
           If Opening Product Wizard, only group is set
           and hackily does not fall into any of the if statements.
           Will route using OpeningProductWizard container.
    */
    // This effect only normalizes/prettifies the URL the user arrived at, so it
    // must replace the current history entry rather than push a new one. Pushing
    // would leave a duplicate entry for the same page, forcing two back presses
    // to leave institution settings.
    if (error && initialState.setting) {
      if (initialState.setting.name !== settingName) {
        history.replace(settingPath(initialState.setting));
      }
      setErrorWasShown(true);
    } else if (!group && !subgroup && !settingName) {
      history.replace(`${groupPath(defaultGroup, defaultSubgroup)}?${qp.toString()}`);
    } else if (group && subgroup) {
      history.replace(`${groupPath(group, subgroup)}?${qp.toString()}`);
    } else if (settingName) {
      history.replace(`${settingPath({ name: settingName })}?${qp.toString()}`);
    }
  }, []);

  useEffect(() => {
    // Remove the error if it is corrected or navigated away from.
    if (error && errorWasShown) {
      setError(null);
    }
  }, [group, settingName]);

  return (
    <div className="ui column grid bgColor--snowGrey" id="setting_page_container">
      <SettingsPageRouter settings={settings} error={error} attemptedValue={attemptedValue} />
    </div>
  );
}

function SettingsPageContainer() {
  const settings = jsonFromDocument("institution_settings");
  const initialState = jsonFromDocument("initial_state");
  Object.keys(settings).forEach((group) => {
    Object.keys(settings[group]).forEach((subgroup) => {
      Object.keys(settings[group][subgroup]).forEach((setting) => {
        if (
          ["JsonDictSettingType", "JsonArraySettingType", "JsonStringSettingType"].includes(
            settings[group][subgroup][setting]?.type
          )
        ) {
          const currSetting = settings[group][subgroup][setting];
          currSetting.value = JSON.stringify(JSON.parse(currSetting.value), null, 4);
        }
      });
    });
  });
  return (
    <div>
      <Router basename={getBaseUrl()}>
        <CompatRouter>
          <SettingsPage settings={settings} initialState={initialState} />
        </CompatRouter>
      </Router>
    </div>
  );
}

export default SettingsPageContainer;
